import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/MeteringRecordsService';
import {useEffect, useState} from 'react';

// FNP-534: read-only module. No create/update/delete mutations — the metering
// aggregator owns the MeteringRecord lifecycle. This hook is list-fetch only.

// Module-scoped global filter store (global-filter-store-pattern). Backend-supported
// fields only (MeteringRecordsFilterInput):
//   state      -> MeteringRecordState (OPEN | CLOSED)
//   customerId -> ID
//   meterId    -> ID (not surfaced in the form yet, but the path supports it)
// meterCode + date-range filtering are deferred to FNP-538 (need a GSI). No default
// filter is seeded — admins see all records on first visit.
let globalFilters: Record<string, string> = {};
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useMeteringRecordsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {filters, handleSetFilters, handleClearFilters};
};

export const useMeteringRecords = () => {
    const {filters} = useMeteringRecordsFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = {column: 'created', direction: 'desc' as const};

    // INFINITE QUERY — filter object is part of the cache key, so changing the
    // filter refetches automatically.
    const meteringRecordsQuery = useInfiniteQuery({
        queryKey: ['meteringRecords', keyword, filters, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, sort, filter: filters, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (meteringRecordsQuery.hasNextPage) {
            meteringRecordsQuery.fetchNextPage();
        }
    };

    const reload = () => {
        meteringRecordsQuery.refetch();
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = meteringRecordsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: meteringRecordsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: meteringRecordsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reload,
        handleFilterByKeyword,
    };
};
